// =====================================================
// ForestAssociationsTable
// Descripción: Tabla de asociaciones forestales desde CKAN
// =====================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCKANData } from '@/lib/ckan/hybrid-query';
import {
  getProvincias,
  getMunicipios,
} from '@/lib/ckan/ckan-storage';
import { Search, MapPin, Building2, Download, Loader2, AlertCircle, HardDrive, Cloud } from 'lucide-react';

export interface ForestAssociationsTableProps {
  height?: number;
}

export function ForestAssociationsTable({ height = 600 }: ForestAssociationsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvincia, setSelectedProvincia] = useState<string>('');
  const [selectedMunicipio, setSelectedMunicipio] = useState<string>('');

  // Query: Asociaciones usando HYBRID QUERY (transparente: PostgreSQL o Storage)
  const { data: queryResult, isLoading, error } = useQuery({
    queryKey: ['forest-associations', selectedProvincia, selectedMunicipio, searchTerm],
    queryFn: async () => {
      const result = await getCKANData('asociaciones_forestales', {
        limit: 1000, // Límite por si hay muchos datos
      });

      if (!result.success) {
        throw new Error(result.error || 'Error fetching data');
      }

      return result;
    },
  });

  // Extraer datos y metadatos
  const allAsociaciones = queryResult?.data || [];
  const storageStrategy = queryResult?.metadata.storage_strategy;

  // Filtrar en memoria (porque obtenemos todos los datos de forma transparente)
  const asociaciones = allAsociaciones.filter((asoc: any) => {
    // Filtro por provincia
    if (selectedProvincia && asoc.provincia !== selectedProvincia) return false;

    // Filtro por municipio
    if (selectedMunicipio && asoc.municipio !== selectedMunicipio) return false;

    // Filtro por búsqueda de texto
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesAsociacion = asoc.asociacion?.toLowerCase().includes(search);
      const matchesDomicilio = asoc.domicilio?.toLowerCase().includes(search);
      const matchesMunicipio = asoc.municipio?.toLowerCase().includes(search);
      const matchesProvincia = asoc.provincia?.toLowerCase().includes(search);

      if (!matchesAsociacion && !matchesDomicilio && !matchesMunicipio && !matchesProvincia) {
        return false;
      }
    }

    return true;
  });

  // Query: Provincias
  const { data: provincias } = useQuery({
    queryKey: ['provincias'],
    queryFn: getProvincias,
  });

  // Query: Municipios (filtra por provincia si está seleccionada)
  const { data: municipios } = useQuery({
    queryKey: ['municipios', selectedProvincia],
    queryFn: () => getMunicipios(selectedProvincia || undefined),
    enabled: !!selectedProvincia,
  });

  // Exportar a CSV
  const handleExportCSV = () => {
    if (!asociaciones || asociaciones.length === 0) return;

    const headers = ['Asociación', 'Domicilio', 'Municipio', 'Provincia'];
    const rows = asociaciones.map(a => [
      a.asociacion,
      a.domicilio || '',
      a.municipio,
      a.provincia,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `asociaciones-forestales-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Reset municipio cuando cambia provincia
  const handleProvinciaChange = (value: string) => {
    setSelectedProvincia(value);
    setSelectedMunicipio(''); // Reset municipio
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center bg-card rounded-lg border"
        style={{ height: `${height}px` }}
      >
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Cargando asociaciones forestales...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center bg-card rounded-lg border"
        style={{ height: `${height}px` }}
      >
        <div className="text-center text-red-600">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p className="font-medium">Error al cargar datos</p>
          <p className="text-sm text-muted-foreground mt-1">
            {error instanceof Error ? error.message : 'Error desconocido'}
          </p>
        </div>
      </div>
    );
  }

  if (!asociaciones || asociaciones.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-card rounded-lg border"
        style={{ height: `${height}px` }}
      >
        <div className="text-center">
          <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium text-muted-foreground">
            No hay datos disponibles
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Sincroniza los datos desde el panel de administración CKAN
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border" style={{ height: `${height}px` }}>
      {/* Header con filtros */}
      <div className="border-b p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Asociaciones Forestales de Castilla y León
            </h3>
            {/* Storage Strategy Badge */}
            {storageStrategy && (
              <span className={`px-2 py-1 text-xs font-medium rounded-md flex items-center gap-1 ${
                storageStrategy === 'postgresql'
                  ? 'bg-purple-100 text-purple-700 border border-purple-300'
                  : 'bg-sky-100 text-sky-700 border border-sky-300'
              }`} title={storageStrategy === 'postgresql' ? 'Datos en PostgreSQL (consultas rápidas)' : 'Datos en Storage (archivo grande)'}>
                {storageStrategy === 'postgresql' ? (
                  <><HardDrive className="w-3 h-3" /> PostgreSQL</>
                ) : (
                  <><Cloud className="w-3 h-3" /> Storage</>
                )}
              </span>
            )}
          </div>
          <button
            onClick={handleExportCSV}
            className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar asociación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Provincia */}
          <select
            value={selectedProvincia}
            onChange={(e) => handleProvinciaChange(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Todas las provincias</option>
            {provincias?.map(provincia => (
              <option key={provincia} value={provincia}>
                {provincia}
              </option>
            ))}
          </select>

          {/* Municipio */}
          <select
            value={selectedMunicipio}
            onChange={(e) => setSelectedMunicipio(e.target.value)}
            disabled={!selectedProvincia}
            className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Todos los municipios</option>
            {municipios?.map(municipio => (
              <option key={municipio} value={municipio}>
                {municipio}
              </option>
            ))}
          </select>
        </div>

        {/* Contador */}
        <p className="text-sm text-muted-foreground">
          Mostrando {asociaciones.length} {asociaciones.length === 1 ? 'asociación' : 'asociaciones'}
        </p>
      </div>

      {/* Tabla */}
      <div className="overflow-auto" style={{ height: `calc(${height}px - 180px)` }}>
        <table className="w-full">
          <thead className="bg-muted/50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Asociación
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Domicilio
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Municipio
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Provincia
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {asociaciones.map((asociacion) => (
              <tr key={asociacion.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span className="font-medium">{asociacion.asociacion}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {asociacion.domicilio || <span className="italic">No especificado</span>}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                    {asociacion.municipio}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {asociacion.provincia}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
